import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { SearchIcon, PlaneIcon, CalendarIcon, ArrowRightLeftIcon, SparklesIcon, MapPinIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
import { NIGERIAN_CITIES, MOCK_FLIGHTS } from '../constants/data';
import { useWallet } from '../hooks/useWallet';
import { formatNaira } from '../utils/formatCurrency';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Modal, ModalFooter } from '../components/ui/Modal';
import { FlightList } from '../components/features/FlightCard';
import type { Flight } from '../types';
// Animated Plane Loader
function PlaneLoader() {
  return <div className="flex flex-col items-center justify-center py-16" data-id="element-1612">
      <motion.div className="relative" animate={{
      x: [0, 100, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1613">
        <motion.div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30" animate={{
        rotate: [0, 5, -5, 0]
      }} transition={{
        duration: 1,
        repeat: Infinity
      }} data-id="element-1614">
          <PlaneIcon className="w-8 h-8 text-white" data-id="element-1615" />
        </motion.div>
        {/* Trail */}
        <motion.div className="absolute top-1/2 right-full w-20 h-1 bg-gradient-to-l from-primary-300 to-transparent rounded-full -translate-y-1/2" animate={{
        opacity: [0.5, 1, 0.5]
      }} transition={{
        duration: 1,
        repeat: Infinity
      }} data-id="element-1616" />
      </motion.div>
      <motion.p className="mt-6 text-body-lg text-surface-600" animate={{
      opacity: [0.5, 1, 0.5]
    }} transition={{
      duration: 1.5,
      repeat: Infinity
    }} data-id="element-1617">
        Searching for the best flights...
      </motion.p>
    </div>;
}
// Empty State Illustration
function EmptyStateIllustration() {
  return <div className="relative w-32 h-32 mx-auto mb-6" data-id="element-1618">
      {/* Background circles */}
      <motion.div className="absolute inset-0 bg-primary-100 rounded-full" animate={{
      scale: [1, 1.1, 1]
    }} transition={{
      duration: 3,
      repeat: Infinity
    }} data-id="element-1619" />
      <motion.div className="absolute inset-2 bg-primary-50 rounded-full" animate={{
      scale: [1, 1.05, 1]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      delay: 0.5
    }} data-id="element-1620" />
      {/* Plane */}
      <motion.div className="absolute inset-0 flex items-center justify-center" animate={{
      y: [0, -5, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1621">
        <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg" data-id="element-1622">
          <PlaneIcon className="w-8 h-8 text-white" data-id="element-1623" />
        </div>
      </motion.div>
      {/* Clouds */}
      <motion.div className="absolute -left-4 top-1/4 w-8 h-4 bg-surface-200 rounded-full" animate={{
      x: [0, 10, 0],
      opacity: [0.5, 1, 0.5]
    }} transition={{
      duration: 4,
      repeat: Infinity
    }} data-id="element-1624" />
      <motion.div className="absolute -right-4 bottom-1/4 w-6 h-3 bg-surface-200 rounded-full" animate={{
      x: [0, -10, 0],
      opacity: [0.5, 1, 0.5]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      delay: 1
    }} data-id="element-1625" />
    </div>;
}
export function SearchPage() {
  const {
    balance,
    canAfford,
    debit
  } = useWallet();
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [passengers, setPassengers] = useState('1');
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [flights, setFlights] = useState<Flight[]>([]);
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const cityOptions = NIGERIAN_CITIES.map(city => ({
    value: city.code,
    label: `${city.name} (${city.code})`
  }));
  const handleSearch = async () => {
    setIsSearching(true);
    setHasSearched(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    const filteredFlights = MOCK_FLIGHTS.filter(flight => {
      if (origin && flight.origin !== origin) return false;
      if (destination && flight.destination !== destination) return false;
      return true;
    });
    setFlights(filteredFlights.length > 0 ? filteredFlights : MOCK_FLIGHTS);
    setIsSearching(false);
  };
  const handleBookFlight = async () => {
    if (!selectedFlight) return;
    setIsBooking(true);
    try {
      await debit(selectedFlight.price, `Booking - ${selectedFlight.airline.name} ${selectedFlight.flightNumber}`, {
        bookingId: `BKG-${Date.now()}`,
        flightNumber: selectedFlight.flightNumber
      });
      setBookingSuccess(true);
      toast.success('Flight booked successfully!');
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error('Failed to book flight. Please try again.');
    } finally {
      setIsBooking(false);
    }
  };
  const swapCities = () => {
    const temp = origin;
    setOrigin(destination);
    setDestination(temp);
  };
  return <div className="min-h-screen bg-gradient-to-b from-primary-50/50 via-white to-surface-50" data-id="element-1626">
      <Header walletBalance={balance} data-id="element-1627" />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1628">
        {/* Hero Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-8" data-id="element-1629">
          <motion.h1 className="text-display-md font-bold text-surface-900 mb-3" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-1630">
            Book Your Next Flight
          </motion.h1>
          <motion.p className="text-body-lg text-surface-600 max-w-2xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} data-id="element-1631">
            Use your store credit to book flights instantly. No waiting, no
            hassle.
          </motion.p>
        </motion.div>

        {/* Search Form */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-1632">
          <Card variant="elevated" padding="lg" className="mb-8 shadow-xl border-0" data-id="element-1633">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-id="element-1634">
              {/* Origin */}
              <div className="md:col-span-3" data-id="element-1635">
                <Select label="From" placeholder="Select city" options={cityOptions} value={origin} onChange={setOrigin} data-id="element-1636" />
              </div>

              {/* Swap Button */}
              <div className="md:col-span-1 flex items-center justify-center" data-id="element-1637">
                <motion.button onClick={swapCities} className="p-3 text-surface-400 hover:text-primary-500 hover:bg-primary-50 rounded-xl transition-all border border-surface-200 hover:border-primary-200" aria-label="Swap cities" whileHover={{
                scale: 1.05,
                rotate: 180
              }} whileTap={{
                scale: 0.95
              }} data-id="element-1638">
                  <ArrowRightLeftIcon className="w-5 h-5" data-id="element-1639" />
                </motion.button>
              </div>

              {/* Destination */}
              <div className="md:col-span-3" data-id="element-1640">
                <Select label="To" placeholder="Select city" options={cityOptions} value={destination} onChange={setDestination} data-id="element-1641" />
              </div>

              {/* Date */}
              <div className="md:col-span-3" data-id="element-1642">
                <Input label="Departure Date" type="date" value={departureDate} onChange={e => setDepartureDate(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-1644" />} data-id="element-1643" />
              </div>

              {/* Passengers */}
              <div className="md:col-span-2" data-id="element-1645">
                <Select label="Passengers" options={[{
                value: '1',
                label: '1 Passenger'
              }, {
                value: '2',
                label: '2 Passengers'
              }, {
                value: '3',
                label: '3 Passengers'
              }, {
                value: '4',
                label: '4 Passengers'
              }]} value={passengers} onChange={setPassengers} data-id="element-1646" />
              </div>
            </div>

            <motion.div whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.99
          }} data-id="element-1647">
              <Button className="mt-6 shadow-lg shadow-primary-500/20" onClick={handleSearch} loading={isSearching} icon={<SearchIcon className="w-4 h-4" data-id="element-1649" />} size="lg" data-id="element-1648">
                Search Flights
              </Button>
            </motion.div>
          </Card>
        </motion.div>

        {/* Wallet Balance Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="mb-8" data-id="element-1650">
          <Card variant="flat" padding="md" className="bg-gradient-to-r from-primary-500 to-primary-600 border-0 text-white overflow-hidden relative" data-id="element-1651">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10" data-id="element-1652">
              <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '24px 24px'
            }} data-id="element-1653" />
            </div>

            <div className="flex items-center justify-between relative" data-id="element-1654">
              <div className="flex items-center gap-4" data-id="element-1655">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-1656">
                  <PlaneIcon className="w-6 h-6 text-white" data-id="element-1657" />
                </div>
                <div data-id="element-1658">
                  <p className="text-body-sm text-white/80" data-id="element-1659">
                    Your Store Credit Balance
                  </p>
                  <p className="text-heading-lg font-bold text-white tabular-nums" data-id="element-1660">
                    {formatNaira(balance)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg backdrop-blur-sm" data-id="element-1661">
                <SparklesIcon className="w-4 h-4" data-id="element-1662" />
                <span className="text-body-sm font-medium" data-id="element-1663">
                  Book flights instantly
                </span>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Search Results */}
        <AnimatePresence mode="wait" data-id="element-1664">
          {isSearching ? <motion.div key="loading" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1665">
              <PlaneLoader data-id="element-1666" />
            </motion.div> : hasSearched ? <motion.div key="results" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} data-id="element-1667">
              <div className="flex items-center justify-between mb-6" data-id="element-1668">
                <div data-id="element-1669">
                  <h2 className="text-heading-lg text-surface-900" data-id="element-1670">
                    {flights.length} Flights Found
                  </h2>
                  <p className="text-body-sm text-surface-500" data-id="element-1671">
                    Showing best available options
                  </p>
                </div>
              </div>

              <FlightList flights={flights} onBook={setSelectedFlight} walletBalance={balance} loading={false} data-id="element-1672" />
            </motion.div> : <motion.div key="empty" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="text-center py-16" data-id="element-1673">
              <EmptyStateIllustration data-id="element-1674" />
              <h2 className="text-heading-lg text-surface-900 mb-3" data-id="element-1675">
                Find Your Next Flight
              </h2>
              <p className="text-body-lg text-surface-600 max-w-md mx-auto mb-8" data-id="element-1676">
                Search for flights and book instantly using your store credit.
                No waiting, no hassle.
              </p>

              {/* Quick Search Suggestions */}
              <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-1677">
                <span className="text-body-sm text-surface-500" data-id="element-1678">
                  Popular routes:
                </span>
                {[{
              from: 'LOS',
              to: 'ABV',
              label: 'Lagos → Abuja'
            }, {
              from: 'LOS',
              to: 'PHC',
              label: 'Lagos → Port Harcourt'
            }, {
              from: 'ABV',
              to: 'KAN',
              label: 'Abuja → Kano'
            }].map(route => <motion.button key={route.label} onClick={() => {
              setOrigin(route.from);
              setDestination(route.to);
            }} className="px-4 py-2 bg-surface-100 hover:bg-primary-50 text-surface-700 hover:text-primary-600 rounded-lg text-body-sm font-medium transition-colors" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1679">
                    {route.label}
                  </motion.button>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </main>

      {/* Booking Modal */}
      <Modal isOpen={!!selectedFlight && !bookingSuccess} onClose={() => setSelectedFlight(null)} title="Confirm Booking" size="md" data-id="element-1680">
        {selectedFlight && <>
            <div className="space-y-4" data-id="element-1681">
              <div className="p-4 bg-gradient-to-r from-primary-50 to-surface-50 rounded-xl border border-primary-100" data-id="element-1682">
                <div className="flex items-center gap-3 mb-3" data-id="element-1683">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center" data-id="element-1684">
                    <PlaneIcon className="w-5 h-5 text-primary-600" data-id="element-1685" />
                  </div>
                  <div data-id="element-1686">
                    <span className="text-body-md font-semibold text-surface-900" data-id="element-1687">
                      {selectedFlight.airline.name}{' '}
                      {selectedFlight.flightNumber}
                    </span>
                    <p className="text-body-sm text-surface-600" data-id="element-1688">
                      {selectedFlight.originCity} →{' '}
                      {selectedFlight.destinationCity}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 p-4 bg-surface-50 rounded-xl" data-id="element-1689">
                <div className="flex justify-between" data-id="element-1690">
                  <span className="text-body-md text-surface-600" data-id="element-1691">
                    Flight Price
                  </span>
                  <span className="text-body-md font-semibold text-surface-900 tabular-nums" data-id="element-1692">
                    {formatNaira(selectedFlight.price)}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-1693">
                  <span className="text-body-md text-surface-600" data-id="element-1694">
                    Your Balance
                  </span>
                  <span className="text-body-md font-semibold text-surface-900 tabular-nums" data-id="element-1695">
                    {formatNaira(balance)}
                  </span>
                </div>
                <div className="border-t border-surface-200 pt-3 flex justify-between" data-id="element-1696">
                  <span className="text-body-md font-semibold text-surface-900" data-id="element-1697">
                    Balance After
                  </span>
                  <span className={`text-body-md font-bold tabular-nums ${canAfford(selectedFlight.price) ? 'text-success-600' : 'text-error-600'}`} data-id="element-1698">
                    {formatNaira(balance - selectedFlight.price)}
                  </span>
                </div>
              </div>

              {!canAfford(selectedFlight.price) && <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2" data-id="element-1699">
                  <AlertCircleIcon className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" data-id="element-1700" />
                  <div data-id="element-1701">
                    <p className="text-body-sm font-medium text-error-700" data-id="element-1702">
                      Insufficient balance
                    </p>
                    <p className="text-caption text-error-600" data-id="element-1703">
                      You need {formatNaira(selectedFlight.price - balance)}{' '}
                      more to book this flight.
                    </p>
                  </div>
                </div>}
            </div>

            <ModalFooter data-id="element-1704">
              <Button variant="secondary" onClick={() => setSelectedFlight(null)} data-id="element-1705">
                Cancel
              </Button>
              <Button onClick={handleBookFlight} loading={isBooking} disabled={!canAfford(selectedFlight.price)} className="shadow-lg shadow-primary-500/20" data-id="element-1706">
                Confirm Booking
              </Button>
            </ModalFooter>
          </>}
      </Modal>

      {/* Success Modal */}
      <Modal isOpen={bookingSuccess} onClose={() => {
      setBookingSuccess(false);
      setSelectedFlight(null);
    }} title="Booking Confirmed!" size="sm" data-id="element-1707">
        <div className="text-center py-4" data-id="element-1708">
          <motion.div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-success-400 to-success-600 rounded-full flex items-center justify-center shadow-lg shadow-success-500/30" initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} data-id="element-1709">
            <CheckCircleIcon className="w-10 h-10 text-white" data-id="element-1710" />
          </motion.div>
          <h3 className="text-heading-lg text-surface-900 mb-2" data-id="element-1711">
            You're all set!
          </h3>
          <p className="text-body-md text-surface-600 mb-6" data-id="element-1712">
            Your flight has been booked successfully. Check your email for
            confirmation details.
          </p>
          <Button fullWidth onClick={() => {
          setBookingSuccess(false);
          setSelectedFlight(null);
        }} className="shadow-lg shadow-primary-500/20" data-id="element-1713">
            Done
          </Button>
        </div>
      </Modal>

      <Footer data-id="element-1714" />
    </div>;
}